import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LoadingController } from '@ionic/angular';
import { LoadingOptions } from '@ionic/core';
import spinnerOptions from '../utils/spinner';

export type ActionCallback = (doneFn: () => void) => void;
export type ObservableCallback<T> = () => Observable<T>;

@Injectable({
  providedIn: 'root'
})
export class LoadingService {

  constructor(public loadingController: LoadingController) {}

  // The caller is responsible for calling the done() callback when it is finished loading
  loadWhile(actionCallback: ActionCallback, opts: LoadingOptions = spinnerOptions()): void {
    this.loadingController.create(opts).then((loadingSpinner: HTMLIonLoadingElement) => {
      loadingSpinner.present().then(() => {
        actionCallback(this.doneCallbackProvider(loadingSpinner));
      });
    });
  }

  loadUntilPromiseResolves(promise: Promise<any>, opts: LoadingOptions = spinnerOptions()): void {
    this.loadingController.create(opts).then((loadingSpinner: HTMLIonLoadingElement) => {
      loadingSpinner.present().then(() => {
        promise.then(() => loadingSpinner.dismiss()).catch(() => loadingSpinner.dismiss());
      });
    });
  }

  async startLoading(opts: LoadingOptions = spinnerOptions()) {
    const loadingSpinner = await this.loadingController.create(opts);
    await loadingSpinner.present();
    return loadingSpinner;
  }

  doneCallbackProvider = (loadingSpinner: HTMLIonLoadingElement) => () => {
    return loadingSpinner.dismiss();
  }

  // Special case of loadWhile - the caller is responsible only for providing an Observable, which will be returned
  loadWhileObservable<T>(
    observableCallback: ObservableCallback<T>,
    opts: LoadingOptions = spinnerOptions()
  ): Observable<T> {
    return new Observable(obs => {
      this.loadingController.create(opts).then((loadingSpinner: HTMLIonLoadingElement) => {
        loadingSpinner.present().then(() => {
          observableCallback().pipe(finalize(() => {
            loadingSpinner.dismiss();
          })).subscribe(() => {
            obs.next();
          });
        });
      });
    });
  }
}
